import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Autocomplete,
  AutocompleteHighlighter,
} from '../Inputs/AutocompleteInput';
import { formatUserFullName } from '@uplift/utils';
import { useCurrentuser } from '@uplift/contexts';
import { useUserMatchesQuery } from '@uplift/queries';


const useProviders = () => {
  const me = useCurrentuser();
  const { data: matches } = useUserMatchesQuery(me.id, [ 'active' ]);

  return matches?.activeMatches.map(m => ({
    id: m.provider.id,
    firstName: m.provider.firstName,
    lastName: m.provider.lastName,
    preferredName: m.providerProfile.preferredName,
  })) || [];
};

type _providersResponse = ReturnType<typeof useProviders>[0];
const providerSearchFilter = (options: _providersResponse[], { inputValue }: { inputValue: string }) => {
  const terms = inputValue.trim().toLowerCase().split(' ');

  return options.filter(opt =>
    terms.every(term =>
      opt.firstName?.toLowerCase()?.includes(term)
      || opt.lastName?.toLowerCase()?.includes(term)
      || opt.preferredName?.toLowerCase()?.includes(term)
    )
  );
};


export const ProviderSearchAutocomplete: React.VFC<{
  name: string;
  label?: string;
  autoFocus?: boolean;
  placeholder?: string;
  disablePortal?: boolean;
  ['data-testid']?: string;
}> = (props) => {
  const providers = useProviders();
  const handleRenderOption = useCallback((option, state) => (
    <Box display="flex" flexDirection="column">
      <Typography>
        <AutocompleteHighlighter text={formatUserFullName({ ...option, preferredFirstName: option.preferredName })} highlight={state.inputValue} />
      </Typography>
    </Box>
  ), []);

  return (
    <Box position="relative" width="100%">
      <Autocomplete
        fullWidth
        disablePortal
        options={providers || []}
        getOptionLabel={(option: _providersResponse) => formatUserFullName({ ...option, preferredFirstName: option.preferredName })}
        renderOption={handleRenderOption}
        filterOptions={providerSearchFilter}
        {...props}
      />
    </Box>
  );
};
